import { useState, useEffect } from 'react';
import { REEVAL_NOTE_MAX_LENGTH } from '@padcanvas/shared';
import { SCORING_LABELS } from '../scoring-labels';
import { ThumbsRating } from '../components/ThumbsRating';
import {
  AdjustmentWhyFields, FieldDestinations, adjustmentWhyPayload, adjustmentWhyProblem, type AdjustmentWhyValue,
} from '../components/AdjustmentWhyFields';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import type { OverrideScoresInput, OverrideResult, ScoreHistoryItem } from './QuestionResultPage';
import './ScoreActions.css';

const TRIGGERED_BY_LABELS: Record<string, string> = {
  student: 'Student',
  teacher: 'Teacher',
  admin: 'Admin',
  system: 'System',
};

// ── Score diff display (shared by override success + re-eval result) ─

const SCORE_DIFF_KEYS = ['correctnessScore', 'approachScore', 'speedScore', 'finalScore'] as const;

const SCORE_DIFF_LABELS: Record<string, string> = {
  correctnessScore: 'Correctness',
  approachScore: 'Approach',
  speedScore: 'Speed',
  finalScore: 'Final Score',
};

interface ScoreDiffData {
  correctnessScore: { old?: number; new?: number };
  speedScore: { old?: number; new?: number };
  approachScore: { old?: number; new?: number };
  finalScore: { old?: number; new?: number };
}

function ScoreDiffDisplay({ diff, hideUnchanged }: { diff: ScoreDiffData; hideUnchanged?: boolean }) {
  return (
    <div className="adv-reeval-diff">
      {SCORE_DIFF_KEYS.map(key => {
        const d = diff[key];
        if (d.old === undefined && d.new === undefined) return null;
        if (hideUnchanged && d.old === d.new) return null;
        return (
          <div key={key} className="adv-reeval-diff-item">
            <span className="adv-reeval-diff-label">{SCORE_DIFF_LABELS[key]}</span>
            <span className="adv-reeval-diff-old">{d.old ?? '-'}</span>
            <span className="adv-reeval-diff-arrow">&rarr;</span>
            <span className="adv-reeval-diff-new">{d.new ?? '-'}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Override Dialog ──────────────────────────────────────────────────

interface OverrideDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (overrides: OverrideScoresInput) => Promise<OverrideResult>;
  initialScores: {
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
  };
  onSuccess?: () => void;
  /** Ask whose side the problem was on and what exactly — both required for an
   *  override, which replaces the machine's verdict with a person's. */
  askWhy?: boolean;
}

function OverrideDialog({ open, onClose, onSubmit, initialScores, onSuccess, askWhy }: OverrideDialogProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OverrideResult | null>(null);

  const [csScore, setCsScore] = useState('');
  const [csComment, setCsComment] = useState('');
  const [asScore, setAsScore] = useState('');
  const [asComment, setAsComment] = useState('');
  const [ssScore, setSsScore] = useState('');
  const [ssComment, setSsComment] = useState('');
  const [why, setWhy] = useState<AdjustmentWhyValue>({});
  const whyProblem = askWhy ? adjustmentWhyProblem(why, { origin: true, reasonCode: true }) : null;

  // Reset form state whenever the dialog opens with fresh initial scores
  useEffect(() => {
    if (open) {
      setCsScore(initialScores.correctnessScore?.toString() ?? '');
      setCsComment(initialScores.correctnessScoreComment ?? '');
      setAsScore(initialScores.approachScore?.toString() ?? '');
      setAsComment(initialScores.approachScoreComment ?? '');
      setSsScore(initialScores.speedScore?.toString() ?? '');
      setSsComment(initialScores.speedScoreComment ?? '');
      setWhy({});
      setStatus('idle');
      setError(null);
      setResult(null);
    }
  }, [open, initialScores]);

  if (!open) return null;

  const handleSubmit = async () => {
    setStatus('loading');
    setError(null);

    const overrides: OverrideScoresInput = askWhy ? adjustmentWhyPayload(why) : {};
    const csVal = csScore !== '' ? parseInt(csScore) : undefined;
    const asVal = asScore !== '' ? parseInt(asScore) : undefined;
    const ssVal = ssScore !== '' ? parseInt(ssScore) : undefined;

    if (csVal !== undefined && !isNaN(csVal)) overrides.correctnessScore = csVal;
    if (csComment) overrides.correctnessScoreComment = csComment;
    if (asVal !== undefined && !isNaN(asVal)) overrides.approachScore = asVal;
    if (asComment) overrides.approachScoreComment = asComment;
    if (ssVal !== undefined && !isNaN(ssVal)) overrides.speedScore = ssVal;
    if (ssComment) overrides.speedScoreComment = ssComment;

    try {
      const res = await onSubmit(overrides);
      setResult(res);
      setStatus('success');
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Override failed');
      setStatus('error');
    }
  };

  return (
    <div className="adv-dialog-backdrop">
      <div className="adv-dialog">
        <div className="adv-dialog-header">
          <h3>Override Scores</h3>
        </div>
        <div className="adv-dialog-body">
          {status === 'success' && result ? (
            <div className="adv-override-success">
              <p className="adv-override-success-msg">Scores updated successfully.</p>
              <ScoreDiffDisplay diff={result.scoreDiff} hideUnchanged />
            </div>
          ) : (
            <div className="adv-override-grid">
              <div className="adv-override-field">
                <label>{SCORING_LABELS.correctness} (0-10)</label>
                <input type="number" min={0} max={10} value={csScore} onChange={e => setCsScore(e.target.value)} />
                <textarea placeholder="Comment (optional)" value={csComment} onChange={e => setCsComment(e.target.value)} rows={2} />
              </div>
              <div className="adv-override-field">
                <label>{SCORING_LABELS.approach} (0-10)</label>
                <input type="number" min={0} max={10} value={asScore} onChange={e => setAsScore(e.target.value)} />
                <textarea placeholder="Comment (optional)" value={asComment} onChange={e => setAsComment(e.target.value)} rows={2} />
              </div>
              <div className="adv-override-field">
                <label>{SCORING_LABELS.speed} (0-10)</label>
                <input type="number" min={0} max={10} value={ssScore} onChange={e => setSsScore(e.target.value)} />
                <textarea placeholder="Comment (optional)" value={ssComment} onChange={e => setSsComment(e.target.value)} rows={2} />
              </div>
              {askWhy && (
                <div className="adv-override-why">
                  <AdjustmentWhyFields value={why} onChange={setWhy} originRequired reasonRequired disabled={status === 'loading'} />
                </div>
              )}
            </div>
          )}
          {status === 'error' && error && (
            <div className="adv-reeval-error">{error}</div>
          )}
        </div>
        <div className="adv-dialog-footer">
          {status === 'success' ? (
            <button className="adv-btn primary" onClick={onClose}>Close</button>
          ) : (
            <>
              <button className="adv-btn secondary" onClick={onClose} disabled={status === 'loading'}>Cancel</button>
              <button className="adv-btn primary" onClick={handleSubmit} disabled={status === 'loading' || !!whyProblem} title={whyProblem ?? undefined}>
                {status === 'loading' ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Re-evaluate Dialog ───────────────────────────────────────────────

interface ReEvaluateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (note?: string, why?: AdjustmentWhyValue) => void;
  /** Also ask why, for our records — optional on a re-evaluation. */
  askWhy?: boolean;
  /** What the note starts as — the teacher's step marks that say a line was
   *  misread. Theirs to edit or clear; only an explicit re-evaluation sends it. */
  initialNote?: string;
}

/** Collects the optional note before firing the re-run: what the first reading
 *  got wrong. Ungated here — a teacher or admin writing "she wrote 5, not S" is
 *  usually the only source of that reading. Shared by the teacher's attempt
 *  pages and the admin console, so both say the same thing about where each
 *  field goes. See docs/FeatureDesigns/solution-analysis/reevaluation-note.md. */
function ReEvaluateDialog({ open, onClose, onConfirm, askWhy, initialNote }: ReEvaluateDialogProps) {
  const [note, setNote] = useState('');
  const [why, setWhy] = useState<AdjustmentWhyValue>({});
  const whyProblem = askWhy ? adjustmentWhyProblem(why) : null;

  useEffect(() => { if (open) { setNote(initialNote ?? ''); setWhy({}); } }, [open, initialNote]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Re-evaluate"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!!whyProblem}
            title={whyProblem ?? undefined}
            onClick={() => {
              onConfirm(note.trim() || undefined, askWhy ? adjustmentWhyPayload(why) : undefined);
              onClose();
            }}
          >
            Re-evaluate
          </Button>
        </>
      }
    >
      <div className="adv-reeval">
        <p className="adv-reeval-intro">
          The work is read and assessed again. The new reading replaces this one; the old
          one stays in History.
        </p>
        <section className="adv-reeval-sec">
          <div className="adv-reeval-head">
            <span className="adv-reeval-title">What was misread? <span className="adv-reeval-opt">(optional)</span></span>
            <FieldDestinations ai student={false} />
          </div>
          <p className="adv-reeval-note-desc">
            Given to the re-run as an unverified claim about what the work says — it makes the
            model look again at the part you point to. It does not change the answer key or the
            marking standard, and asking for a score has no effect.
          </p>
          <textarea
            className="adv-reeval-note-input"
            placeholder='e.g. "in step 2 the working reads 3x, not 8x" or "the final answer is on the second page"'
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={REEVAL_NOTE_MAX_LENGTH}
            rows={4}
            autoFocus
          />
        </section>
        {askWhy && <AdjustmentWhyFields value={why} onChange={setWhy} />}
      </div>
    </Modal>
  );
}

// ── Feedback Reason Dialog ───────────────────────────────────────────

interface FeedbackReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason?: string, why?: AdjustmentWhyValue) => Promise<void>;
  /** A teacher's thumbs-down disputes the reading: also ask whose side the
   *  problem was on, for our records. Optional, like the reason itself. */
  askWhy?: boolean;
}

/** Collects what was wrong with a thumbs-down rating, AFTER the rating itself
 *  is already saved — the words are optional, so Skip still leaves the flag
 *  behind for someone to look at. A dialog rather than an inline row: a
 *  one-line input left no room to actually explain the concern. */
function FeedbackReasonDialog({ open, onClose, onSubmit, askWhy }: FeedbackReasonDialogProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [why, setWhy] = useState<AdjustmentWhyValue>({});
  const whyProblem = askWhy ? adjustmentWhyProblem(why) : null;

  useEffect(() => { if (open) { setReason(''); setSaving(false); setWhy({}); } }, [open]);

  if (!open) return null;

  const submit = async () => {
    setSaving(true);
    try { await onSubmit(reason.trim() || undefined, askWhy ? adjustmentWhyPayload(why) : undefined); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="adv-dialog-backdrop">
      <div className="adv-dialog">
        <div className="adv-dialog-header">
          <h3>What's wrong with it?</h3>
        </div>
        <div className="adv-dialog-body">
          <textarea
            className="adv-reeval-note-input"
            placeholder="Optional — say what's wrong, if you can"
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={4}
            autoFocus
            onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
          />
          {askWhy && (
            <div className="adv-reeval-why">
              <AdjustmentWhyFields value={why} onChange={setWhy} disabled={saving} />
            </div>
          )}
        </div>
        <div className="adv-dialog-footer">
          <button className="adv-btn secondary" onClick={onClose} disabled={saving}>Skip</button>
          <button className="adv-btn primary" onClick={() => void submit()} disabled={saving || !!whyProblem} title={whyProblem ?? undefined}>
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Score History Dialog ─────────────────────────────────────────────

interface ScoreHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  onLoad: () => Promise<{ activeVersion: number; history: ScoreHistoryItem[] }>;
}

function ScoreHistoryDialog({ open, onClose, onLoad }: ScoreHistoryDialogProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{ activeVersion: number; history: ScoreHistoryItem[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    onLoad()
      .then(result => setData(result))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  return (
    <div className="adv-dialog-backdrop">
      <div className="adv-dialog adv-dialog-wide">
        <div className="adv-dialog-header">
          <h3>Score History</h3>
        </div>
        <div className="adv-dialog-body">
          {loading && <p>Loading...</p>}
          {error && <div className="adv-reeval-error">{error}</div>}
          {data && data.history.length === 0 && <p>No score history available.</p>}
          {data && data.history.length > 0 && (
            <table className="adv-history-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>By</th>
                  <th>Date</th>
                  <th>Correctness</th>
                  <th>Approach</th>
                  <th>Speed</th>
                  <th>Final</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map(item => (
                  <tr key={item.version} className={item.version === data.activeVersion ? 'active-version' : ''}>
                    <td>
                      v{item.version}
                      {item.version === data.activeVersion && <span className="adv-active-badge">active</span>}
                    </td>
                    <td>
                      {TRIGGERED_BY_LABELS[item.triggeredBy] || item.triggeredBy}
                      {/* What they said was misread. A score that moved is only
                          reviewable next to the claim that moved it. */}
                      {item.note && <div className="adv-history-note">“{item.note}”</div>}
                    </td>
                    <td>{new Date(item.triggeredAt).toLocaleString()}</td>
                    <td>
                      {item.correctnessScore ?? '-'}
                      {item.correctnessScoreComment && <div className="adv-history-comment">{item.correctnessScoreComment}</div>}
                    </td>
                    <td>
                      {item.approachScore ?? '-'}
                      {item.approachScoreComment && <div className="adv-history-comment">{item.approachScoreComment}</div>}
                    </td>
                    <td>
                      {item.speedScore ?? '-'}
                      {item.speedScoreComment && <div className="adv-history-comment">{item.speedScoreComment}</div>}
                    </td>
                    <td>{item.finalScore ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="adv-dialog-footer">
          <button className="adv-btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Score Actions (links + dialogs) ──────────────────────────────────

export interface ScoreActionsProps {
  hasFeedback: boolean;
  onOverrideScores?: (overrides: OverrideScoresInput) => Promise<OverrideResult>;
  onLoadScoreHistory?: () => Promise<{ activeVersion: number; history: ScoreHistoryItem[] }>;
  /** `note` — the optional "what was misread" the dialog collected, for the
   *  model; `why` — for our records, when `askWhy`. */
  onReEvaluate?: (note?: string, why?: AdjustmentWhyValue) => void;
  /** Teacher/admin pages: ask why on re-evaluate and override. */
  askWhy?: boolean;
  reEvaluateStatus?: 'idle' | 'loading' | 'success' | 'error';
  /** Open the region corrector for this question. Present only on an attempt
   *  built from an uploaded script, where the work under a question can be the
   *  wrong question's — the one case a re-evaluation alone cannot fix, because
   *  it would re-read the same wrong region.
   *  See docs/FeatureDesigns/assignments-tests/solution-region-correction-target.md. */
  onCorrectRegion?: () => void;
  initialOverrideScores: {
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
  };
  /** When provided, an inline 👍/👎 control renders alongside the History /
   *  Override / Re-evaluate links, sending its rating to the system as app
   *  feedback. Used by both student and teacher attempt-detail pages.
   *
   *  A thumbs-DOWN then asks what was wrong with it: the rating alone opens a
   *  flag for someone to look at, and a flag that says only "bad" is a
   *  question, not a report. `comment` is what that prompt collected. */
  submissionId?: string;
  onRateFeedback?: (submissionId: string, rating: 'positive' | 'negative', comment?: string, why?: AdjustmentWhyValue) => Promise<void>;
  existingRating?: 'positive' | 'negative' | null;
}

export interface ScoreActionsHandle {
  hasHistory: boolean;
  overrideSucceeded: boolean;
}

export function ScoreActions({
  hasFeedback,
  onOverrideScores,
  onLoadScoreHistory,
  onReEvaluate,
  askWhy,
  reEvaluateStatus = 'idle',
  onCorrectRegion,
  initialOverrideScores,
  submissionId,
  onRateFeedback,
  existingRating,
}: ScoreActionsProps) {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reEvalOpen, setReEvalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [overrideSucceeded, setOverrideSucceeded] = useState(false);
  // Whether the thumbs-down reason dialog is open — asked for AFTER the
  // rating is saved. The words are optional: demanding them is how flags
  // stop being left at all.
  const [askReason, setAskReason] = useState(false);

  // Preload history count to decide whether to show the link
  useEffect(() => {
    if (!onLoadScoreHistory || !hasFeedback) return;
    onLoadScoreHistory()
      .then(result => { if (result.history.length > 1) setHasHistory(true); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showHistory = hasHistory || overrideSucceeded || reEvaluateStatus === 'success';

  /** Re-sends the same rating with the words attached. The rating is already
   *  saved and the server upserts one entry per user, so a failure here loses
   *  the comment and nothing else. */
  const saveReason = async (reason?: string, why?: AdjustmentWhyValue) => {
    if (!onRateFeedback || !submissionId) return;
    await onRateFeedback(submissionId, 'negative', reason, why);
  };

  return (
    <>
      <div className="adv-score-actions">
        {onLoadScoreHistory && hasFeedback && showHistory && (
          <a
            href="#"
            className="adv-score-action-link"
            onClick={e => { e.preventDefault(); setHistoryOpen(true); }}
          >
            History
          </a>
        )}
        {onOverrideScores && (
          <a
            href="#"
            className="adv-score-action-link"
            onClick={e => { e.preventDefault(); setOverrideOpen(true); }}
          >
            Override
          </a>
        )}
        {onReEvaluate && (
          <a
            href="#"
            className={`adv-score-action-link ${reEvaluateStatus === 'loading' ? 'disabled' : ''}`}
            onClick={e => { e.preventDefault(); if (reEvaluateStatus !== 'loading') setReEvalOpen(true); }}
          >
            {reEvaluateStatus === 'loading' ? 'Re-evaluating...' : 'Re-evaluate'}
          </a>
        )}
        {onCorrectRegion && (
          <a
            href="#"
            className="adv-score-action-link"
            onClick={e => { e.preventDefault(); onCorrectRegion(); }}
          >
            Wrong working?
          </a>
        )}
        {onRateFeedback && submissionId && (
          <ThumbsRating
            initialRating={existingRating ?? null}
            onRate={(rating) => onRateFeedback(submissionId, rating)}
            onAfterRate={(rating) => {
              if (rating !== 'negative') return;
              setAskReason(true);
            }}
            positiveTitle="This assessment is right"
            negativeTitle="Something is wrong with this assessment"
            size="sm"
          />
        )}
      </div>

      {onRateFeedback && submissionId && (
        <FeedbackReasonDialog
          open={askReason}
          onClose={() => setAskReason(false)}
          onSubmit={saveReason}
          askWhy={askWhy}
        />
      )}

      {onOverrideScores && (
        <OverrideDialog
          open={overrideOpen}
          onClose={() => setOverrideOpen(false)}
          onSubmit={onOverrideScores}
          initialScores={initialOverrideScores}
          onSuccess={() => { setOverrideSucceeded(true); setHasHistory(true); }}
          askWhy={askWhy}
        />
      )}

      {onReEvaluate && (
        <ReEvaluateDialog
          open={reEvalOpen}
          onClose={() => setReEvalOpen(false)}
          onConfirm={onReEvaluate}
          askWhy={askWhy}
        />
      )}

      {onLoadScoreHistory && (
        <ScoreHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onLoad={onLoadScoreHistory}
        />
      )}
    </>
  );
}

// The dialogs, for the teacher attempt page's action bar (shared-ui/teacher-attempt).
export { ScoreDiffDisplay, OverrideDialog, ReEvaluateDialog, FeedbackReasonDialog, TRIGGERED_BY_LABELS };
export type { ScoreDiffData };
